"use client";
import { useState, useEffect, useRef } from "react";
import { zustandStore } from "../zustand-store";
import { IoClose } from "react-icons/io5";
import { MdOutlineSync } from "react-icons/md";
import axios from "axios";
import TextComponent from "./TextComponent";
import { useRouter } from "next/navigation";
import { updateSelectedMailchimpKey } from "../(dashboard)/dashboard/utils/mailchimp";

const ProcessingProgress = () => {
  const { data, actions } = zustandStore();
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const { status, progress, currentStep, error, trackerId, reportId } =
    data.processingStatus || {
      status: "idle",
      progress: 0,
      currentStep: "",
      error: undefined,
      trackerId: null,
      reportId: null,
    };

  // auto-hiding when completed
  useEffect(() => {
    if (status === "completed" || status === "failed") {
      if (status === "completed" && reportId) {
        const url = `/view-report/${reportId}`;
        window.open(url, "_blank");
      }
      const fetchData = async () => {
        try {
          const keyId =
            data.processingStatus?.mailchimpKeyId ||
            data.mailchimpProfile?.selectedMailchimpKeyId;

          if (!keyId) {
            console.error("Missing mailchimpKeyId for fetching processed data");
            return;
          }

          const response = await axios.get(`/api/processed-data/${keyId}`);
          actions.setProcessedData(response.data?.processedData || []);
          updateAPIKeys(keyId);
        } catch (err) {
          console.log("Failed to fetch processed data:", err);
        }
      };

      fetchData();

      // Conditionally set a timer to hide the component.
      // Do not hide if processing is complete and a report is available.
      // Only auto-close on completed without reportId, not on failed
      if (status === "completed" && !reportId) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setIsMinimized(false);
          actions.setIsProcessingProgressVisible(false);
          actions.updateProcessingStatus({
            status: "idle",
            progress: 0,
            currentStep: "",
            reportId: null,
          });
        }, 10000);
        return () => clearTimeout(timer);
      }
    }
  }, [status, actions, reportId]);

  useEffect(() => {
    if (status === "processing") {
      // If we have no trackerId, it might be a stale state or initial load glith
      if (!trackerId) {
        console.log("[ProcessingProgress] Processing status with no trackerId - likely stale state");
        // Add a small timeout to allow for trackerId to arrive if it's just race condition
        const timer = setTimeout(() => {
          // If still no trackerId after delay, reset
          if (!data.processingStatus?.trackerId) {
            console.log("[ProcessingProgress] Auto-closing stale processing state");
            setIsVisible(false);
            actions.setIsProcessingProgressVisible(false);
            actions.updateProcessingStatus({ status: "idle" });
          }
        }, 3000);
        return () => clearTimeout(timer);
      }

      setIsVisible(true);
      setIsMinimized(false);
      actions.setIsProcessingProgressVisible(true);
    }
  }, [status, trackerId, actions, data.processingStatus?.trackerId]);

  // Watch for "No data found" error and open modal
  useEffect(() => {
    if (status === "failed" && error === "No data found") {
      // Close this progress component
      setIsVisible(false);
      setIsMinimized(false);
      actions.setIsProcessingProgressVisible(false);
      actions.updateProcessingStatus({
        status: "idle", // Reset processing status so we don't loop
        progress: 0,
        currentStep: "",
        error: undefined
      });

      // Open the no-data modal
      actions.openModal({
        modalName: "mailchimp-api-key",
        modalProps: { reason: "no-data" }
      });
    }
  }, [status, error, actions]);

  // to resume polling if processing is still ongoing after a refresh
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (status === "processing" && trackerId) {
      intervalId = setInterval(async () => {
        try {
          const statusResponse = await axios.get(
            `/api/mailchimp/fetch-and-process?trackerId=${trackerId}`,
          );
          const {
            status,
            progress,
            message,
            error,
            processedData,
            mailchimpKeyId,
          } = statusResponse.data;

          // Update processing status
          actions.updateProcessingStatus({
            status,
            progress,
            currentStep: message,
            error,
            mailchimpKeyId,
          });

          // Optionally update processed data
          if (processedData && Object.keys(processedData).length > 0) {
            actions.setProcessedData(processedData);
          }

          if (status === "completed" || status === "failed") {
            clearInterval(intervalId);
          }
        } catch (error) {
          console.error("Error polling resumed status:", error);
          clearInterval(intervalId);
        }
      }, 10000);
    }
    return () => clearInterval(intervalId);
  }, [status, trackerId, actions]);

  if (status === "idle" || !isVisible) return null;

  const handleClose = () => {
    // Only minimize if processing, otherwise fully close
    if (status === "processing") {
      setIsAnimating(true);
      // Wait for animation to complete before setting minimized state
      setTimeout(() => {
        setIsMinimized(true);
        setIsAnimating(false);
      }, 300);
    } else {
      setIsVisible(false);
      setIsMinimized(false);
      actions.setIsProcessingProgressVisible(false);
      if (status === "completed") {
        actions.updateProcessingStatus({
          status: "idle",
          progress: 0,
          currentStep: "",
        });
      }
    }
  };

  const handleExpand = () => {
    setIsAnimating(true);
    setIsMinimized(false);
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // Fetch the latest unread error notification to ensure we have the correct ID
      const notificationsResponse = await axios.get("/api/notifications");
      const errorNotification = notificationsResponse.data.notifications?.find(
        (n: any) => n.type === "DATA_SYNC_ERROR" && !n.isRead,
      );

      if (errorNotification) {
        await axios.put(`/api/notifications/${errorNotification._id}/read`);
        actions.updateProcessingStatus({ notificationId: null });
      }

      // First update UI to show retrying status
      actions.updateProcessingStatus({
        status: "processing",
        progress: 0,
        currentStep: "Retrying Mailchimp data processing",
        error: "",
      });

      // Get the mailchimp key ID from your store or component state
      // This assumes you have access to the mailchimpKeyId and user email
      // You may need to modify this based on your actual state management
      const mailchimpKeyId =
        data.processingStatus?.mailchimpKeyId ||
        data.mailchimpProfile?.selectedMailchimpKeyId;
      const email = data.userDetails?.email;

      if (!mailchimpKeyId || !email) {
        throw new Error("Missing required information for retry");
      }

      // Make the API call to retry the process
      const fetchProcessResponse = await axios.post(
        "/api/mailchimp/fetch-and-process",
        {
          email,
          mailchimpKeyId,
        },
      );

      const trackerId = fetchProcessResponse.data.trackerId;

      // Update processing status with new tracker ID
      actions.updateProcessingStatus({
        status: "processing",
        progress: 1,
        trackerId,
        currentStep: "Starting Mailchimp data processing",
      });
    } catch (error) {
      console.error("Error retrying process:", error);
      actions.updateProcessingStatus({
        status: "failed",
        progress: 0,
        currentStep: "",
        error: "Failed to retry. Please try again later.",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const updateAPIKeys = async (selectedKeyId: string) => {
    try {
      const res = await updateSelectedMailchimpKey(
        data?.userDetails?.email || "",
        selectedKeyId,
      );
      console.log("🚀 ~ updateAPIKeys ~ res:", res);
    } catch (error) {
      console.error("Error updating API keys:", error);
    }
  };

  // Minimized chip view
  if (isMinimized && !isAnimating) {
    return (
      <div
        ref={chipRef}
        onClick={handleExpand}
        className="fixed bottom-0 left-0 right-0 z-50 flex cursor-pointer items-center justify-center gap-2.5 rounded-t-[12px] bg-[#FFDD22] px-4 py-2 shadow-lg transition-all duration-300 ease-in-out hover:pb-3 hover:shadow-xl md:left-auto md:right-4 md:mr-20 md:w-auto md:justify-start"
        style={{
          animation: "chipSlideIn 0.3s ease-out forwards",
        }}
      >
        {/* Circular progress indicator - smaller size (24px) */}
        <div className="relative h-6 w-6">
          <svg className="h-6 w-6 -rotate-90 transform">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#00000015"
              strokeWidth="2.5"
              fill="none"
            />
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="#000000"
              strokeWidth="2.5"
              fill="none"
              strokeDasharray={`${(progress / 100) * 62.8} 62.8`}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <MdOutlineSync className="h-3 w-3 animate-spin text-black" />
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <TextComponent type="caption" className="font-bold uppercase tracking-wider text-black opacity-70">
            Processing
          </TextComponent>
          <TextComponent type="h4" className="font-black text-black">
            {progress}%
          </TextComponent>
        </div>

        {/* CSS for animations */}
        <style jsx>{`
          @keyframes chipSlideIn {
            0% {
              transform: translateY(100%);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  // Full expanded view
  return (
    <div
      ref={containerRef}
      className={`fixed bottom-0 left-0 right-0 z-50 w-full rounded-t-lg bg-white p-4 shadow-lg transition-all duration-300 ease-in-out md:bottom-4 md:left-auto md:right-4 md:mr-20 md:w-80 md:rounded-lg
      ${isAnimating && !isMinimized ? "animate-expandIn" : ""}
      ${isAnimating && isMinimized ? "animate-minimizeOut" : ""}
      ${isVisible && !isAnimating ? "translate-y-0 opacity-100" : ""}
      ${!isVisible ? "translate-y-8 opacity-0" : ""}`}
      style={
        isAnimating
          ? {
            animation: isMinimized
              ? "minimizeOut 0.3s ease-in forwards"
              : "expandIn 0.3s ease-out forwards",
          }
          : {}
      }
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">Processing Mailchimp Data</span>
        <div className="flex items-center gap-2">
          <span>{progress}%</span>
          {/* Close Button */}
          {(status === "completed" ||
            status == "failed" ||
            status === "processing") && (
              <button
                className="text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Close"
                onClick={handleClose}
              >
                <IoClose />
              </button>
            )}
        </div>
      </div>

      <div className="h-2.5 w-full rounded-full bg-gray-200">
        <div
          className="h-2.5 rounded-full bg-blue-600 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <TextComponent type="body" className="mt-2 text-kd-secondary-3">
        {currentStep}
      </TextComponent>
      {status === "completed" && reportId && (
        <div className="flex justify-end">
          <button
            onClick={() => window.open(`/view-report/${reportId}`, "_blank")}
            id="view_report"
            className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            View Report
          </button>
        </div>
      )}
      {error && status === "failed" && (
        <TextComponent
          type="h4"
          className="mt-2 flex items-center justify-between space-x-2"
        >
          <TextComponent type="h4" className="text-sm text-red-500">
            {error}
          </TextComponent>
          {error === "No data found" && (() => {
            // Trigger modal open in a useEffect-like way or immediate action
            // Best practice is to do this in a useEffect, but for inline rendering we can't.
            // So we'll use an effect hook above to watch for this specific error state.
            return null; // Don't render anything here, handled by effect
          })()}
          {error !== "No data found" &&
            (data.processingStatus?.mailchimpKeyId ||
              data.mailchimpProfile?.selectedMailchimpKeyId) &&
            data.userDetails?.email && (
              <button
                className="cursor-pointer text-kd-lte-blue"
                onClick={handleRetry}
                disabled={isRetrying}
              >
                {isRetrying ? "Retrying..." : "Retry"}
              </button>
            )}
        </TextComponent>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes minimizeOut {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0.2) translateX(100px) translateY(50px);
            opacity: 0;
          }
        }
        @keyframes expandIn {
          0% {
            transform: scale(0.2) translateX(100px) translateY(50px);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ProcessingProgress;
